<script setup lang="ts">
  import { computed, useListeners } from 'vue';
  import { ISector } from '@/interfaces/ISector';
  import { IPosTableView } from '@/interfaces/IPosTableView';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import {
    computeContentExtent,
    IFloorContentExtent,
    MAX_CELL_PX,
    MIN_PANEL_BASIS_PX,
    PANEL_CHROME_PX,
    TARGET_CELL_PX,
  } from '@/utils/floor-plan.utils';
  import PosFloorTableTile from './PosFloorTableTile.vue';

  const props = defineProps<{
    sectors: ISector[];
    tables: IPosTableView[];
  }>();

  const emit = defineEmits<{
    (e: 'table-click', table: IPosTableView): void;
    /** Long-press / right-click on a tile (POS V2 orders preview). */
    (e: 'table-longpress', table: IPosTableView): void;
  }>();

  const restaurantStore = useRestaurantStore();
  const listeners = useListeners();

  const DEFAULT_SECTOR_COLOR = '#a3a3a3';
  const EMPTY_STYLE: Record<string, string> = {};

  /**
   * Forward the tile's secondary action only when a consumer listens, so the
   * tile keeps the browser's default right-click behavior in the old POS.
   */
  function longPressListenersFor(table: IPosTableView) {
    if (!listeners['table-longpress']) return {};
    return { longpress: () => emit('table-longpress', table) };
  }

  interface IPlacedEntry {
    table: IPosTableView;
    style: Record<string, string>;
  }

  interface ISectorView {
    sector: ISector;
    extent: IFloorContentExtent | null;
    /** Padding-top % that gives the body its extent-proportional height. */
    bodyPaddingTop: string | null;
    entries: IPlacedEntry[];
    panelStyle: Record<string, string>;
  }

  /** Content-based width: small sectors share a row, large ones wrap full. */
  function panelStyleFor(
    extent: IFloorContentExtent | null
  ): Record<string, string> {
    if (!extent) {
      return {
        flex: `1 1 ${MIN_PANEL_BASIS_PX}px`,
        maxWidth: '320px',
      };
    }
    const basis = Math.max(
      extent.cols * TARGET_CELL_PX + PANEL_CHROME_PX,
      MIN_PANEL_BASIS_PX
    );
    const cap = Math.max(
      extent.cols * MAX_CELL_PX + PANEL_CHROME_PX,
      MIN_PANEL_BASIS_PX
    );
    // Plain px cap (no CSS min(); Chrome <79): flex-shrink already prevents
    // the panel from overflowing a narrower container.
    return {
      flex: `1 1 ${basis}px`,
      maxWidth: `${cap}px`,
    };
  }

  const sectorsSorted = computed(() =>
    [...props.sectors].sort(
      (a, b) => a.index - b.index || a.name.localeCompare(b.name)
    )
  );

  const liveSectorIds = computed(() => new Set(props.sectors.map((s) => s.id)));

  /** Placed = has live sector + both coordinates (mirror of the API rule). */
  function isPlaced(table: IPosTableView): boolean {
    return (
      table.sectorId !== null &&
      table.posX !== null &&
      table.posY !== null &&
      liveSectorIds.value.has(table.sectorId)
    );
  }

  // Percentage-based positioning relative to the content extent: tiles keep
  // their proportions at any panel size without measuring the container
  // (the panel fixes its aspect ratio to the extent).
  const sectorViews = computed<ISectorView[]>(() =>
    sectorsSorted.value.map((sector) => {
      const placed = props.tables.filter(
        (table) => table.sectorId === sector.id && isPlaced(table)
      );

      const extent = computeContentExtent(
        placed.map((table) => ({
          posX: table.posX ?? 0,
          posY: table.posY ?? 0,
          w: table.w,
          h: table.h,
        }))
      );

      const entries: IPlacedEntry[] = placed.map((table) => ({
        table,
        // extent is always non-null here (placed is non-empty).
        style: extent
          ? {
              left: `${
                (((table.posX ?? 0) - extent.colStart) / extent.cols) * 100
              }%`,
              top: `${
                (((table.posY ?? 0) - extent.rowStart) / extent.rows) * 100
              }%`,
              width: `${(Math.max(table.w, 1) / extent.cols) * 100}%`,
              height: `${(Math.max(table.h, 1) / extent.rows) * 100}%`,
            }
          : EMPTY_STYLE,
      }));

      return {
        sector,
        extent,
        // Padding-top % instead of CSS aspect-ratio (Chrome <88 renders a
        // zero-height body otherwise): the tiles are absolutely positioned,
        // so the padding alone provides the extent-proportional height.
        bodyPaddingTop: extent
          ? `${(extent.rows / extent.cols) * 100}%`
          : null,
        entries,
        panelStyle: panelStyleFor(extent),
      };
    })
  );

  /** Everything not on a canvas stays reachable below the sectors. */
  const unplacedTables = computed(() =>
    props.tables.filter((table) => !isPlaced(table))
  );
</script>

<template>
  <div class="pos-floor-plan">
    <div class="sectors">
      <div
        v-for="view in sectorViews"
        :key="view.sector.id"
        class="sector"
        :style="view.panelStyle"
      >
        <div class="sector-header">
          <span
            class="color-dot"
            :style="{
              backgroundColor: view.sector.color || DEFAULT_SECTOR_COLOR,
            }"
          ></span>
          <span class="sector-name">{{ view.sector.name }}</span>
        </div>
        <div
          class="sector-body"
          :class="{ 'sector-body--empty': !view.extent }"
          :style="
            view.bodyPaddingTop
              ? { paddingTop: view.bodyPaddingTop }
              : undefined
          "
        >
          <PosFloorTableTile
            v-for="entry in view.entries"
            :key="entry.table.id"
            :table="entry.table"
            :style="entry.style"
            class="placed-tile"
            @click="emit('table-click', entry.table)"
            v-on="longPressListenersFor(entry.table)"
          />
          <span v-if="!view.extent" class="sector-empty-label">
            {{ $t('posTables.sectorEmpty') }}
          </span>
        </div>
      </div>
    </div>

    <div v-if="unplacedTables.length" class="unplaced">
      <div class="unplaced-header">{{ $t('posTables.unplaced') }}</div>
      <div class="unplaced-list">
        <PosFloorTableTile
          v-for="table in unplacedTables"
          :key="table.id"
          :table="table"
          compact
          @click="emit('table-click', table)"
          v-on="longPressListenersFor(table)"
        />
      </div>
    </div>

    <div class="plan-footer">
      <v-btn
        icon
        small
        color="grey"
        :to="`/${restaurantStore.restaurant.stringId}/tables/plan`"
        class="edit-plan"
      >
        <v-icon small>mdi-pencil</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-floor-plan {
    @apply tw-space-y-4;

    // Flex gaps are emulated with margins throughout this component: it must
    // render correctly on old POS devices (Chrome <84 has no flexbox gap).
    .sectors {
      @apply tw-flex tw-flex-wrap tw-items-start;
      margin: -0.375rem;

      .sector {
        @apply tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-p-3;
        margin: 0.375rem;

        .sector-header {
          @apply tw-flex tw-items-center tw-mb-2;

          .color-dot {
            @apply tw-w-2 tw-h-2 tw-rounded-full tw-shrink-0 tw-mr-2;
          }

          .sector-name {
            @apply tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500 tw-truncate;
          }
        }

        .sector-body {
          @apply tw-relative tw-w-full tw-rounded-lg tw-bg-neutral-50;

          &.sector-body--empty {
            @apply tw-h-24 tw-flex tw-items-center tw-justify-center;
          }

          .placed-tile {
            @apply tw-absolute;
          }

          .sector-empty-label {
            @apply tw-text-xs tw-text-neutral-400;
          }
        }
      }
    }

    .unplaced {
      .unplaced-header {
        @apply tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500 tw-mb-2;
      }

      .unplaced-list {
        @apply tw-flex tw-flex-wrap;
        margin: -0.25rem;

        > * {
          margin: 0.25rem;
        }
      }
    }

    .plan-footer {
      @apply tw-flex tw-items-center tw-justify-start;
    }
  }
</style>
